// src/components/RouterAgent.js
// Agent Routeur — /router-agent. Sorts every incoming e-mail: spam, routing
// subject, AI agent (or a redirect). Zones: header · « À vérifier » (brands
// left unrouted, subjects without description) · period + tiles
// (GET /api/agent-router/logs/stats) · views (?tab=):
//   routing        — the instructions per scope (without active instructions
//                    covering a brand or a mailbox, nothing is routed) and
//                    the routing subjects with their volume;
//   classification — the independent ticket-topic taxonomy
//                    (GET/PUT /api/logistics/topics-config);
//   journal        — breakdowns per subject / agent + every decision
//                    (filters, table, drawer, CSV).
// Disabling or deleting instructions cuts routing: it goes through a
// confirmation that says what changes (next e-mails only).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Ban, Check, ChevronRight, CircleCheck, Download, Forward, ListChecks, Mail, Pencil, Plus, RefreshCw, Shapes,
  Tags, Trash2, TriangleAlert, Workflow, X,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, DescriptionList, Drawer, EmptyState, Field, Kpi, Meter, Modal, OverflowMenu,
  PageHeader, Pagination, Segmented, Switch, Tabs, Toast,
} from '../design/ui';
import {
  LOGS_PAGE_SIZE, PERIODS, addRedirect, brandIdOf, breakdownRows, decisionOf, instructionCreatePayload,
  instructionFormFrom, instructionPayload, routerAttention, routerCoverage, routerKpis, routerLogQuery,
  routerStatsQuery, toggleDefaultTopic, topicsConfigPayload,
} from './routerAgentModel';
import { formatCount, formatPercent, formatStamp } from './clientsShared';
import './RouterAgent.css';

const TABS = ['routing', 'classification', 'journal'];
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const RouterAgent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab = TABS.includes(tabParam) ? tabParam : 'routing';
  const setActiveTab = useCallback((tab) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (tab === 'routing') next.delete('tab'); else next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // ── Data ──
  const [topics, setTopics] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [brands, setBrands] = useState([]);
  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const [topicsConfig, setTopicsConfig] = useState(null);
  const [topicsConfigSaving, setTopicsConfigSaving] = useState(false);
  const [stats, setStats] = useState(undefined); // undefined = loading, null = unavailable
  const [statsDays, setStatsDays] = useState(30);
  const [journalBrand, setJournalBrand] = useState('');
  const [logs, setLogs] = useState(null);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logFilters, setLogFilters] = useState({ topic: '', is_spam: '', source: '' });
  const [selectedLog, setSelectedLog] = useState(null);

  // ── Dialogs ──
  const [topicModal, setTopicModal] = useState(null); // {editing, form:{name,description}, error, saving}
  const [instrModal, setInstrModal] = useState(null); // {editing, form, redirectInput, error, saving}
  const [customTopicForm, setCustomTopicForm] = useState({ name: '', description: '' });
  // {kind: deactivate | delete | deleteTopic | removeCustom, target, busy}
  const [confirm, setConfirm] = useState(null);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((tone, text) => setToast({ tone, text }), []);
  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  // ══════════ Fetchers ══════════

  const fetchTopics = useCallback(async () => {
    try {
      const res = await axios.get('/api/tickets/user-topics', { headers: authHeaders() });
      setTopics(res.data.topics || []);
    } catch (err) {
      console.error('Error fetching topics:', err);
      setTopics([]);
      showToast('danger', t('routerAgent.fetchError', 'Impossible de charger les sujets de routage.'));
    }
  }, [showToast, t]);

  const fetchInstructions = useCallback(async () => {
    try {
      const res = await axios.get('/api/agent-router/instructions', { headers: authHeaders() });
      setInstructions(res.data || []);
    } catch (err) {
      console.error('Error fetching instructions:', err);
      setInstructions([]);
      showToast('danger', t('routerAgent.instr.fetchError', 'Impossible de charger les instructions.'));
    }
  }, [showToast, t]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`/api/agent-router/logs/stats?${routerStatsQuery({ days: statsDays, brand: journalBrand })}`, { headers: authHeaders() });
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setStats(null);
    }
  }, [statsDays, journalBrand]);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`/api/agent-router/logs?${routerLogQuery({ page: logsPage, limit: LOGS_PAGE_SIZE, brand: journalBrand, filters: logFilters })}`, { headers: authHeaders() });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogs([]);
    }
  }, [logsPage, journalBrand, logFilters]);

  const fetchTopicsConfig = useCallback(async () => {
    try {
      const res = await axios.get('/api/logistics/topics-config', { headers: authHeaders() });
      setTopicsConfig(res.data);
    } catch (err) {
      console.error('Error fetching topics config:', err);
      showToast('danger', t('routerAgent.classif.fetchError', 'Impossible de charger la configuration de classification.'));
    }
  }, [showToast, t]);

  useEffect(() => {
    const headers = authHeaders();
    axios.get('/api/brands/list', { headers })
      .then((res) => setBrands(res.data || []))
      .catch((err) => console.error('Error fetching brands:', err));
    axios.get('/api/social/outlook/accounts', { headers })
      .then((res) => setOutlookAccounts(res.data.accounts || []))
      .catch((err) => console.error('Error fetching Outlook accounts:', err));
    fetchTopics();
    fetchInstructions();
  }, [fetchTopics, fetchInstructions]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 'journal') fetchLogs();
  }, [activeTab, fetchLogs]);

  useEffect(() => {
    if (activeTab === 'classification' && topicsConfig === null) fetchTopicsConfig();
  }, [activeTab, topicsConfig, fetchTopicsConfig]);

  // ══════════ Labels ══════════

  const getBrandName = (id) => {
    if (!id) return t('routerAgent.allBrands', 'Toutes les marques');
    const brand = brands.find((b) => brandIdOf(b) === String(id));
    return brand?.name || id;
  };
  const periodLabel = {
    7: t('routerAgent.logs.last7', '7 derniers jours'),
    30: t('routerAgent.logs.last30', '30 derniers jours'),
    90: t('routerAgent.logs.last90', '90 derniers jours'),
    365: t('routerAgent.logs.lastYear', '12 derniers mois'),
  }[statsDays];
  const sourceLabel = (s) => {
    if (s === 'web_form') return t('routerAgent.logs.webForm', 'Formulaire web');
    if (s === 'outlook') return 'Outlook';
    return s || '—';
  };
  const DECISIONS = {
    routed: { tone: 'success', icon: Check, label: t('routerAgent.logs.routed', 'Routé') },
    redirect: { tone: 'info', icon: Forward, label: t('routerAgent.logs.redirected', 'Redirigé') },
    spam: { tone: undefined, icon: Ban, label: t('routerAgent.logs.spam', 'Spam') },
  };
  const decisionBadge = (log) => {
    const d = DECISIONS[decisionOf(log)];
    return <Badge tone={d.tone} icon={d.icon}>{d.label}</Badge>;
  };

  // ══════════ Derived ══════════

  const kpis = routerKpis(stats);
  const coverage = useMemo(() => routerCoverage(instructions, brands), [instructions, brands]);
  const attention = useMemo(() => routerAttention({ instructions, topics, brands }), [instructions, topics, brands]);
  const topicVolumes = stats?.topic_breakdown || null;
  const maxTopicVolume = topicVolumes ? Math.max(1, ...Object.values(topicVolumes)) : 1;
  const missingDescriptions = (topics || []).filter((topic) => !String(topic.description || '').trim()).length;
  const topicFilterOptions = [...new Set([
    ...(topics || []).map((tp) => tp.name),
    ...(logs || []).map((l) => l.topic).filter(Boolean),
  ])];

  // ══════════ Topics CRUD ══════════

  const openTopicModal = (topic = null) => {
    setTopicModal({
      editing: topic,
      form: { name: topic?.name || '', description: topic?.description || '' },
      error: null,
      saving: false,
    });
  };

  const saveTopic = async () => {
    const { form, editing } = topicModal;
    if (!form.name.trim()) {
      setTopicModal((m) => ({ ...m, error: t('routerAgent.nameRequired', 'Le nom du sujet est requis.') }));
      return;
    }
    setTopicModal((m) => ({ ...m, saving: true, error: null }));
    try {
      if (editing) {
        await axios.put(`/api/tickets/user-topics/${editing._id}`, form, { headers: authHeaders() });
        showToast('success', t('routerAgent.topicUpdated', 'Sujet mis à jour.'));
      } else {
        await axios.post('/api/tickets/user-topics', form, { headers: authHeaders() });
        showToast('success', t('routerAgent.topicCreated', 'Sujet créé.'));
      }
      setTopicModal(null);
      fetchTopics();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setTopicModal((m) => ({
        ...m, saving: false,
        error: typeof detail === 'string' ? detail : t('routerAgent.saveError', 'Échec de l’enregistrement du sujet.'),
      }));
    }
  };

  // ══════════ Instructions CRUD ══════════

  const openInstrModal = (doc = null, presetBrandId = '') => {
    setInstrModal({ editing: doc, form: instructionFormFrom(doc, presetBrandId), redirectInput: '', error: null, saving: false });
  };
  const setInstrForm = (patch) => setInstrModal((m) => ({ ...m, form: { ...m.form, ...patch }, error: null }));

  const saveInstruction = async () => {
    const { form, editing } = instrModal;
    if (!form.instructions.trim()) {
      setInstrModal((m) => ({ ...m, error: t('routerAgent.instr.instructionsRequired', 'Les instructions sont requises.') }));
      return;
    }
    setInstrModal((m) => ({ ...m, saving: true, error: null }));
    try {
      if (editing) {
        await axios.put(`/api/agent-router/instructions/${editing.id}`, instructionPayload(form), { headers: authHeaders() });
        showToast('success', t('routerAgent.instr.updated', 'Instructions mises à jour.'));
      } else {
        await axios.post('/api/agent-router/instructions', instructionCreatePayload(form), { headers: authHeaders() });
        showToast('success', t('routerAgent.instr.created', 'Instructions créées — le routage est actif pour cette portée.'));
      }
      setInstrModal(null);
      fetchInstructions();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setInstrModal((m) => ({
        ...m, saving: false,
        error: typeof detail === 'string' ? detail : t('routerAgent.instr.saveError', 'Échec de l’enregistrement des instructions.'),
      }));
    }
  };

  const toggleInstruction = async (doc, next) => {
    if (!next) {
      setConfirm({ kind: 'deactivate', target: doc, busy: false });
      return;
    }
    try {
      await axios.put(`/api/agent-router/instructions/${doc.id}`, { is_active: true }, { headers: authHeaders() });
      fetchInstructions();
    } catch (err) {
      showToast('danger', t('routerAgent.instr.toggleError', 'Échec de l’activation/désactivation.'));
    }
  };

  // ══════════ Classification ══════════

  const saveTopicsConfig = async (nextConfig) => {
    setTopicsConfigSaving(true);
    try {
      const res = await axios.put('/api/logistics/topics-config', topicsConfigPayload(nextConfig), { headers: authHeaders() });
      setTopicsConfig(res.data);
      showToast('success', t('routerAgent.classif.saved', 'Configuration de classification enregistrée.'));
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      showToast('danger', typeof detail === 'string' ? detail : t('routerAgent.classif.saveError', 'Échec de l’enregistrement de la classification.'));
      return false;
    } finally {
      setTopicsConfigSaving(false);
    }
  };

  const addCustomTopic = () => {
    saveTopicsConfig({
      ...topicsConfig,
      custom_topics: [
        ...(topicsConfig.custom_topics || []),
        { name: customTopicForm.name.trim(), description: customTopicForm.description.trim() },
      ],
    });
    setCustomTopicForm({ name: '', description: '' });
  };

  // ══════════ Confirmations ══════════

  const confirmChange = async () => {
    const { kind, target } = confirm;
    setConfirm((c) => ({ ...c, busy: true }));
    try {
      if (kind === 'deactivate') {
        await axios.put(`/api/agent-router/instructions/${target.id}`, { is_active: false }, { headers: authHeaders() });
        fetchInstructions();
      } else if (kind === 'delete') {
        await axios.delete(`/api/agent-router/instructions/${target.id}`, { headers: authHeaders() });
        showToast('success', t('routerAgent.instr.deleted', 'Instructions supprimées.'));
        fetchInstructions();
      } else if (kind === 'deleteTopic') {
        await axios.delete(`/api/tickets/user-topics/${target._id}`, { headers: authHeaders() });
        showToast('success', t('routerAgent.topicDeleted', 'Sujet supprimé.'));
        fetchTopics();
      } else if (kind === 'removeCustom') {
        setConfirm(null);
        await saveTopicsConfig({
          ...topicsConfig,
          custom_topics: (topicsConfig.custom_topics || []).filter((c) => c.slug !== target.slug),
        });
        return;
      }
      setConfirm(null);
    } catch (err) {
      setConfirm(null);
      showToast('danger', {
        deactivate: t('routerAgent.instr.toggleError', 'Échec de l’activation/désactivation.'),
        delete: t('routerAgent.instr.deleteError', 'Échec de la suppression des instructions.'),
        deleteTopic: t('routerAgent.deleteError', 'Échec de la suppression du sujet.'),
      }[kind]);
    }
  };

  // ══════════ CSV export ══════════

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`/api/agent-router/logs/export?${routerLogQuery({ brand: journalBrand, filters: logFilters })}`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'agent_router_logs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting logs:', err);
      showToast('danger', t('aiAutomationUi.router.logs.exportError', 'Échec de l’export CSV.'));
    }
  };

  const setFilter = (patch) => { setLogFilters((f) => ({ ...f, ...patch })); setLogsPage(1); };
  const openJournal = (isSpam) => { setFilter({ is_spam: isSpam }); setActiveTab('journal'); };

  // ══════════ Zones ══════════

  const subtitle = kpis && kpis.total > 0
    ? t('aiAutomationUi.router.subtitle', '{{routed}} courriels routés et {{spam}} spams écartés · {{period}}', {
      routed: formatCount(kpis.routed), spam: formatCount(kpis.spam), period: periodLabel,
    })
    : t('routerAgent.subtitle', 'Trie chaque ticket entrant : spam, sujet de routage, agent IA.');

  const attentionText = (item) => (item.kind === 'unrouted'
    ? {
      title: t('aiAutomationUi.router.attention.unrouted', '{{count}} marques ne sont pas routées', { count: item.count }),
      detail: t('aiAutomationUi.router.attention.unroutedDetail', 'Aucune instruction active ne couvre leurs courriels : {{names}}', { names: item.names.join(', ') }),
      action: () => (item.brandId ? openInstrModal(null, item.brandId) : setActiveTab('routing')),
      label: item.brandId ? t('aiAutomationUi.router.attention.add', 'Ajouter') : t('aiAutomationUi.router.attention.see', 'Voir'),
    }
    : {
      title: t('aiAutomationUi.router.attention.nodesc', '{{count}} sujets sans description', { count: item.count }),
      detail: t('aiAutomationUi.router.attention.nodescDetail', 'Le routeur choisit le sujet d’après sa description : {{names}}', { names: item.names.join(', ') }),
      action: () => (item.topic ? openTopicModal(item.topic) : setActiveTab('routing')),
      label: item.topic ? t('aiAutomationUi.router.attention.complete', 'Compléter') : t('aiAutomationUi.router.attention.see', 'Voir'),
    });

  const renderAttention = () => {
    if (!attention.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('aiAutomationUi.router.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="ra-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => {
            const text = attentionText(item);
            return (
              <li className="u-row" key={item.key}>
                <Badge tone="warning" dot>{t('aiAutomationUi.router.attention.watch', 'À surveiller')}</Badge>
                <div className="u-row__main">
                  <div className="u-row__title">{text.title}</div>
                  <div className="u-row__meta" title={text.detail}>{text.detail}</div>
                </div>
                <div className="u-row__side">
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={text.action}>
                    {text.label}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };

  const renderPeriodBar = () => (
    <div className="ra-v2__period">
      <Segmented
        label={t('routerAgent.logs.period', 'Période')}
        value={statsDays}
        onChange={setStatsDays}
        options={PERIODS.map((days) => ({
          value: days,
          label: days === 365 ? t('aiAutomationUi.router.period.year', '12 mois') : t('aiAutomationUi.router.period.days', '{{days}} jours', { days }),
        }))}
      />
      <span className="ra-v2__scope">
        {t('aiAutomationUi.router.period.scope', 'Courriels triés par le routeur · {{period}}', { period: periodLabel })}
        {journalBrand && ` · ${getBrandName(journalBrand)}`}
      </span>
    </div>
  );

  const renderKpis = () => {
    if (stats === undefined) {
      return (
        <div className="u-kpi-row ra-v2__kpis" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton ra-v2__kpi-skeleton" />)}
        </div>
      );
    }
    const journal = activeTab === 'journal';
    return (
      <div className="u-kpi-row ra-v2__kpis">
        {kpis && (
          <>
            <Kpi
              icon={Workflow}
              label={t('aiAutomationUi.router.kpi.sorted', 'Courriels triés')}
              value={formatCount(kpis.total)}
              context={t('aiAutomationUi.router.kpi.sortedContext', 'décisions du routeur · {{period}}', { period: periodLabel })}
              active={journal && logFilters.is_spam === ''}
              onClick={() => openJournal('')}
            />
            <Kpi
              icon={CircleCheck}
              tone="success"
              label={t('aiAutomationUi.router.kpi.routed', 'Routés')}
              value={formatCount(kpis.routed)}
              context={kpis.routedRate === null
                ? t('aiAutomationUi.router.kpi.none', 'aucun courriel trié')
                : t('aiAutomationUi.router.kpi.routedContext', '{{rate}} vers un sujet et un agent', { rate: formatPercent(kpis.routedRate) })}
              meter={kpis.routedRate === null ? undefined : { value: kpis.routed, max: kpis.total, tone: 'success', label: t('aiAutomationUi.router.kpi.routed', 'Routés') }}
              active={journal && logFilters.is_spam === 'false'}
              onClick={() => openJournal('false')}
            />
            <Kpi
              icon={Ban}
              tone="neutral"
              label={t('aiAutomationUi.router.kpi.spam', 'Spam écartés')}
              value={formatCount(kpis.spam)}
              context={kpis.spamRate === null ? undefined : t('aiAutomationUi.router.kpi.spamContext', '{{rate}} des courriels triés', { rate: formatPercent(kpis.spamRate) })}
              active={journal && logFilters.is_spam === 'true'}
              onClick={() => openJournal('true')}
            />
          </>
        )}
        {topics !== null && (
          <Kpi
            icon={Tags}
            tone={missingDescriptions ? 'warning' : undefined}
            label={t('aiAutomationUi.router.kpi.topics', 'Sujets de routage')}
            value={formatCount(topics.length)}
            context={!topics.length
              ? t('aiAutomationUi.router.kpi.topicsNone', 'aucun sujet défini')
              : missingDescriptions
                ? t('aiAutomationUi.router.kpi.topicsMissing', '{{count}} sans description', { count: missingDescriptions })
                : t('aiAutomationUi.router.kpi.topicsOk', 'tous décrits')}
            contextTone={missingDescriptions ? 'warning' : undefined}
            active={activeTab === 'routing'}
            onClick={() => setActiveTab('routing')}
          />
        )}
      </div>
    );
  };

  // ---- Routing view ---------------------------------------------------------

  const coverageState = (row) => {
    switch (row.state) {
      case 'covered':
        return <span className="ra-v2__state">{t('routerAgent.cov.coveredByGlobal', 'Couvert par « Toutes les marques »')}</span>;
      case 'off':
        return row.isGlobal
          ? <span className="ra-v2__state">{t('aiAutomationUi.router.cov.globalOff', 'Aucune instruction : les courriels des marques sans instructions ne sont pas routés')}</span>
          : <Badge tone="warning" dot>{t('routerAgent.cov.noInstructions', 'Aucune instruction — tickets non routés')}</Badge>;
      default:
        return null;
    }
  };

  const renderInstructionLine = (doc, row) => {
    const mailboxes = doc.apply_to_emails || [];
    const redirects = doc.redirect_emails || [];
    let text = doc.instructions;
    if (!doc.is_active) {
      if (row.state === 'fallback') text = t('aiAutomationUi.router.cov.followsGlobal', 'Le routage suit « Toutes les marques »');
      else if (row.state === 'off') {
        text = row.isGlobal
          ? t('aiAutomationUi.router.cov.globalOffShort', 'Courriels sans instructions de marque non routés')
          : t('aiAutomationUi.router.cov.notRouted', 'Courriels de cette marque non routés');
      }
    }
    return (
      <div key={doc.id} className={`ra-v2__line${doc.is_active ? '' : ' is-off'}`}>
        {doc.is_active
          ? <Badge tone="success" dot>{t('aiAutomationUi.router.cov.active', 'Actives')}</Badge>
          : <Badge>{t('aiAutomationUi.router.cov.inactive', 'Désactivées')}</Badge>}
        <span className="ra-v2__line-text" title={doc.instructions}>{text}</span>
        {mailboxes.length > 0 && (
          <span className="u-tag ra-v2__tag" title={mailboxes.join(', ')}>
            <Mail aria-hidden="true" />
            {mailboxes.length === 1 ? mailboxes[0] : t('routerAgent.cov.mailboxes', '{{total}} boîtes mail', { total: mailboxes.length })}
          </span>
        )}
        {redirects.length > 0 && (
          <span className="u-tag ra-v2__tag" title={redirects.join(', ')}>
            <Forward aria-hidden="true" />
            {t('aiAutomationUi.router.cov.redirects', '{{count}} redirections', { count: redirects.length })}
          </span>
        )}
        <span className="ra-v2__line-side">
          <Switch
            id={`ra-instr-${doc.id}`}
            checked={doc.is_active}
            onChange={(next) => toggleInstruction(doc, next)}
            label={<span className="ra-v2__sr">{doc.is_active ? t('routerAgent.instr.deactivate', 'Désactiver') : t('routerAgent.instr.activate', 'Activer')}</span>}
          />
          <OverflowMenu
            label={t('aiAutomationUi.router.instrActions', 'Actions des instructions')}
            items={[
              { label: t('common.edit', 'Modifier'), icon: Pencil, onClick: () => openInstrModal(doc) },
              { separator: true },
              { label: t('common.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => setConfirm({ kind: 'delete', target: doc, busy: false }) },
            ]}
          />
        </span>
      </div>
    );
  };

  const renderRouting = () => (
    <>
      <Card
        icon={Workflow}
        title={t('aiAutomationUi.router.cov.title', 'Instructions de routage')}
        description={t('routerAgent.cov.subtitle', 'Le routeur ne traite un courriel que si des instructions actives couvrent sa marque ou sa boîte mail — sans instructions, aucun routage.')}
        actions={(
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openInstrModal()}>
            <Plus aria-hidden="true" />
            {t('routerAgent.instr.create', 'Créer des instructions')}
          </button>
        )}
        flush
      >
        {instructions === null ? (
          <div className="ra-v2__skeleton" aria-busy="true">
            {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
          </div>
        ) : (
          <ul className="u-list">
            {coverage.map((row) => (
              <li key={row.brandId || 'global'} className="u-row ra-v2__cov-row">
                <div className="ra-v2__cov-scope">
                  <span className="ra-v2__cov-name">{row.isGlobal ? t('routerAgent.allBrands', 'Toutes les marques') : row.brand.name}</span>
                  {row.isGlobal && <span className="ra-v2__cov-hint">{t('routerAgent.cov.fallback', 'portée par défaut')}</span>}
                </div>
                <div className="ra-v2__cov-body">
                  {row.instructions.map((doc) => renderInstructionLine(doc, row))}
                  {!row.instructions.length && (
                    <div className="ra-v2__line">
                      {coverageState(row)}
                      <span className="ra-v2__line-side">
                        <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => openInstrModal(null, row.brandId)}>
                          <Plus aria-hidden="true" />
                          {t('routerAgent.cov.add', 'Ajouter')}
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        icon={Tags}
        title={t('aiAutomationUi.router.topics.title', 'Sujets de routage')}
        badge={topics ? <Badge>{topics.length}</Badge> : null}
        description={t('routerAgent.topicsSubtitle', 'Le routeur choisit le sujet d’après sa description ; les sujets alimentent aussi les onglets du tableau de bord.')}
        actions={(
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openTopicModal()}>
            <Plus aria-hidden="true" />
            {t('routerAgent.createTopic', 'Créer un sujet')}
          </button>
        )}
        footerNote={topics && topics.length > 0 && topicVolumes
          ? t('routerAgent.topicVolumeNote', 'Volumes : tickets routés sur la période « {{period}} ».', { period: periodLabel })
          : null}
        flush
      >
        {topics === null ? (
          <div className="ra-v2__skeleton" aria-busy="true">
            {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
          </div>
        ) : topics.length === 0 ? (
          <EmptyState
            icon={Tags}
            title={t('aiAutomationUi.router.topics.emptyTitle', 'Aucun sujet de routage')}
            action={(
              <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => openTopicModal()}>
                <Plus aria-hidden="true" />
                {t('routerAgent.createTopic', 'Créer un sujet')}
              </button>
            )}
          >
            {t('routerAgent.noTopicsDesc', 'Aucun sujet pour le moment. Créez vos segments (ex. : Avant vente, Après vente) pour que le routeur classe les tickets.')}
          </EmptyState>
        ) : (
          <ul className="u-list">
            {topics.map((topic) => {
              const volume = topicVolumes ? topicVolumes[topic.name] : null;
              const described = String(topic.description || '').trim();
              return (
                <li key={topic._id} className="u-row">
                  <div className="u-row__main">
                    <div className="u-row__title">{topic.name}</div>
                    {described ? (
                      <div className="u-row__meta" title={topic.description}>{topic.description}</div>
                    ) : (
                      <div className="u-row__meta ra-v2__missing">
                        <TriangleAlert aria-hidden="true" />
                        {t('routerAgent.descriptionMissing', 'À compléter — le routeur choisit le sujet d’après sa description.')}
                      </div>
                    )}
                  </div>
                  <div className="u-row__side">
                    {typeof volume === 'number' && (
                      <span className="ra-v2__volume" title={t('routerAgent.topicVolumeTitle', '{{total}} tickets routés · {{period}}', { total: volume, period: periodLabel })}>
                        <span className="u-num">{formatCount(volume)}</span>
                        <Meter value={volume} max={maxTopicVolume} label={topic.name} />
                      </span>
                    )}
                    <OverflowMenu
                      label={t('aiAutomationUi.router.topicActions', 'Actions du sujet')}
                      items={[
                        { label: t('routerAgent.editTopic', 'Modifier le sujet'), icon: Pencil, onClick: () => openTopicModal(topic) },
                        { separator: true },
                        { label: t('common.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => setConfirm({ kind: 'deleteTopic', target: topic, busy: false }) },
                      ]}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </>
  );

  // ---- Classification view --------------------------------------------------

  const renderClassification = () => {
    if (topicsConfig === null) {
      return (
        <section className="u-card">
          <div className="ra-v2__skeleton" aria-busy="true">
            {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton" />)}
          </div>
        </section>
      );
    }
    const defaults = topicsConfig.default_topics || [];
    const enabledCount = defaults.filter((topic) => !(topicsConfig.disabled_topics || []).includes(topic.slug)).length;
    const custom = topicsConfig.custom_topics || [];
    return (
      <>
        <Card
          icon={Shapes}
          title={t('routerAgent.classif.title', 'Classification des tickets')}
          badge={topicsConfig.classification_enabled
            ? <Badge tone="success" dot>{t('aiAutomationUi.router.classif.on', 'Active')}</Badge>
            : <Badge>{t('aiAutomationUi.router.classif.off', 'Désactivée')}</Badge>}
          description={t('routerAgent.classif.intro', 'Les sujets standards décrivent l’objet du ticket (suivi, marchandise endommagée, retour…). Axe indépendant du routage : activer la classification ne modifie jamais vos sujets de routage.')}
          footerNote={t('aiAutomationUi.router.classif.future', 'S’applique aux nouveaux tickets entrants : les tickets déjà ouverts gardent leur sujet.')}
        >
          <div className="ra-v2__switch-row">
            <Switch
              id="ra-classif"
              checked={topicsConfig.classification_enabled}
              disabled={topicsConfigSaving}
              onChange={(next) => saveTopicsConfig({ ...topicsConfig, classification_enabled: next })}
              label={(
                <span className="ra-v2__switch-text">
                  <strong>{t('routerAgent.classif.toggle', 'Activer la classification automatique')}</strong>
                  <span>{t('routerAgent.classif.toggleDesc', 'Chaque nouveau ticket entrant est étiqueté avec le sujet standard le plus adapté.')}</span>
                </span>
              )}
            />
          </div>
          <div className="ra-v2__section">
            <div className="ra-v2__section-head">
              <h3>{t('routerAgent.classif.defaultTopics', 'Sujets standards')}</h3>
              <span className="ra-v2__section-count u-num">
                {t('aiAutomationUi.router.classif.enabled', '{{count}} sur {{total}} utilisés', { count: enabledCount, total: defaults.length })}
              </span>
            </div>
            <div className="ra-v2__chips">
              {defaults.map((topic) => {
                const enabled = !(topicsConfig.disabled_topics || []).includes(topic.slug);
                return (
                  <button
                    key={topic.slug}
                    type="button"
                    className={`ra-v2__chip${enabled ? ' is-on' : ''}`}
                    aria-pressed={enabled}
                    disabled={topicsConfigSaving}
                    title={topic.description}
                    onClick={() => saveTopicsConfig(toggleDefaultTopic(topicsConfig, topic.slug))}
                  >
                    {enabled && <Check aria-hidden="true" />}
                    {topic.name}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>

        <Card
          icon={Tags}
          title={t('routerAgent.classif.customTopics', 'Sujets personnalisés')}
          badge={custom.length ? <Badge>{custom.length}</Badge> : null}
          description={t('aiAutomationUi.router.classif.customDesc', 'Vos propres sujets, ajoutés aux sujets standards. La description aide l’IA à classer.')}
          flush
        >
          {custom.length === 0 ? (
            <p className="ra-v2__muted ra-v2__pad">{t('routerAgent.classif.noCustom', 'Aucun sujet personnalisé pour le moment.')}</p>
          ) : (
            <ul className="u-list">
              {custom.map((topic) => (
                <li key={topic.slug} className="u-row">
                  <div className="u-row__main">
                    <div className="u-row__title">{topic.name}</div>
                    {topic.description && <div className="u-row__meta" title={topic.description}>{topic.description}</div>}
                  </div>
                  <div className="u-row__side">
                    <button
                      type="button"
                      className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                      disabled={topicsConfigSaving}
                      onClick={() => setConfirm({ kind: 'removeCustom', target: topic, busy: false })}
                      title={t('common.delete', 'Supprimer')}
                      aria-label={t('common.delete', 'Supprimer')}
                    >
                      <Trash2 aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <form
            className="ra-v2__add"
            onSubmit={(e) => { e.preventDefault(); if (customTopicForm.name.trim()) addCustomTopic(); }}
          >
            <input
              type="text"
              className="u-input"
              placeholder={t('routerAgent.classif.customName', 'Nom du sujet')}
              aria-label={t('routerAgent.classif.customName', 'Nom du sujet')}
              value={customTopicForm.name}
              onChange={(e) => setCustomTopicForm((f) => ({ ...f, name: e.target.value }))}
            />
            <input
              type="text"
              className="u-input"
              placeholder={t('routerAgent.classif.customDescription', 'Description (aide l’IA à classifier)')}
              aria-label={t('routerAgent.classif.customDescription', 'Description (aide l’IA à classifier)')}
              value={customTopicForm.description}
              onChange={(e) => setCustomTopicForm((f) => ({ ...f, description: e.target.value }))}
            />
            <button type="submit" className="u-btn u-btn--secondary" disabled={topicsConfigSaving || !customTopicForm.name.trim()}>
              <Plus aria-hidden="true" />
              {t('routerAgent.classif.addCustom', 'Ajouter')}
            </button>
          </form>
        </Card>
      </>
    );
  };

  // ---- Journal view ---------------------------------------------------------

  const renderBreakdown = (title, breakdown, unknownLabel) => {
    const rowsList = breakdownRows(breakdown);
    if (!rowsList.length) return null;
    return (
      <div className="ra-v2__breakdown">
        <h3 className="u-overline">{title}</h3>
        <ul className="ra-v2__bars">
          {rowsList.map((row) => (
            <li key={row.label}>
              <span className="ra-v2__bar-label" title={row.unknown ? unknownLabel : row.label}>{row.unknown ? unknownLabel : row.label}</span>
              <Meter value={row.count} max={rowsList[0].count} label={row.unknown ? unknownLabel : row.label} />
              <span className="ra-v2__bar-value u-num">{formatCount(row.count)}</span>
              <span className="ra-v2__bar-share u-num">{formatPercent(row.share)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderJournal = () => {
    const hasBreakdowns = stats && (Object.keys(stats.topic_breakdown || {}).length > 0 || Object.keys(stats.assistant_breakdown || {}).length > 0);
    return (
      <>
        {hasBreakdowns && (
          <Card
            icon={ListChecks}
            title={t('aiAutomationUi.router.breakdown.title', 'Répartition des courriels routés')}
            description={t('aiAutomationUi.router.breakdown.desc', 'Spam exclus · {{period}} · part des courriels routés', { period: periodLabel })}
          >
            <div className="u-grid-2">
              {renderBreakdown(t('aiAutomationUi.router.breakdown.byTopic', 'Par sujet'), stats.topic_breakdown, t('aiAutomationUi.router.breakdown.noTopic', 'Sans sujet'))}
              {renderBreakdown(t('aiAutomationUi.router.breakdown.byAgent', 'Par agent IA'), stats.assistant_breakdown, t('aiAutomationUi.router.breakdown.noAgent', 'Sans agent'))}
            </div>
          </Card>
        )}

        <section className="u-card">
          <div className="u-toolbar">
            <Segmented
              label={t('routerAgent.logs.decision', 'Décision')}
              value={logFilters.is_spam}
              onChange={(isSpam) => setFilter({ is_spam: isSpam })}
              options={[
                { value: '', label: t('routerAgent.logs.all', 'Tous') },
                { value: 'false', label: t('routerAgent.logs.notSpam', 'Non spam') },
                { value: 'true', label: t('aiAutomationUi.router.logs.spamOnly', 'Spam') },
              ]}
            />
            {brands.length > 0 && (
              <select className="u-select" value={journalBrand} onChange={(e) => { setJournalBrand(e.target.value); setLogsPage(1); }} aria-label={t('routerAgent.logs.brand', 'Marque')}>
                <option value="">{t('routerAgent.allBrands', 'Toutes les marques')}</option>
                {brands.map((b) => <option key={brandIdOf(b)} value={brandIdOf(b)}>{b.name}</option>)}
              </select>
            )}
            <select className="u-select" value={logFilters.topic} onChange={(e) => setFilter({ topic: e.target.value })} aria-label={t('routerAgent.logs.topicCol', 'Sujet')}>
              <option value="">{t('aiAutomationUi.router.logs.allTopics', 'Tous les sujets')}</option>
              {topicFilterOptions.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
            </select>
            <select className="u-select" value={logFilters.source} onChange={(e) => setFilter({ source: e.target.value })} aria-label={t('routerAgent.logs.source', 'Source')}>
              <option value="">{t('aiAutomationUi.router.logs.allSources', 'Toutes les sources')}</option>
              <option value="outlook">Outlook</option>
              <option value="web_form">{t('routerAgent.logs.webForm', 'Formulaire web')}</option>
            </select>
            <span className="u-toolbar__spacer" />
            <button
              type="button"
              className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
              onClick={() => { fetchStats(); fetchLogs(); }}
              title={t('routerAgent.logs.refresh', 'Actualiser')}
              aria-label={t('routerAgent.logs.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleExportCSV}>
              <Download aria-hidden="true" />
              {t('routerAgent.logs.exportCSV', 'Export CSV')}
            </button>
          </div>
          {logs === null ? (
            <div className="ra-v2__skeleton" aria-busy="true">
              {Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}
            </div>
          ) : logs.length === 0 ? (
            <EmptyState icon={Workflow} title={t('aiAutomationUi.router.logs.noMatch', 'Aucune décision de routage ne correspond aux filtres.')}>
              {t('aiAutomationUi.router.logs.emptyHint', 'Une décision apparaît ici à chaque courriel trié par le routeur.')}
            </EmptyState>
          ) : (
            <>
              <div className="u-table-scroll">
                <table className="u-table ra-v2__table">
                  <thead>
                    <tr>
                      <th style={{ width: '140px' }}>{t('routerAgent.logs.date', 'Date')}</th>
                      <th style={{ width: '14%' }}>{t('routerAgent.logs.source', 'Source')}</th>
                      <th style={{ width: '22%' }}>{t('routerAgent.logs.topicCol', 'Sujet')}</th>
                      <th style={{ width: '20%' }}>{t('aiAutomationUi.router.logs.agent', 'Agent IA')}</th>
                      <th style={{ width: '24%' }}>{t('routerAgent.logs.decision', 'Décision')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log.id}
                        className={`ra-v2__row${selectedLog?.id === log.id ? ' is-selected' : ''}`}
                        tabIndex={0}
                        onClick={() => setSelectedLog(log)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setSelectedLog(log); }}
                      >
                        <td className="u-num">{formatStamp(log.created_at) || '—'}</td>
                        <td>{sourceLabel(log.source)}</td>
                        <td title={log.topic || ''}>{log.topic || <span className="u-muted">—</span>}</td>
                        <td title={log.assistant_name || ''}>{log.assistant_name || <span className="u-muted">—</span>}</td>
                        <td title={log.redirected_to || undefined}>
                          {decisionBadge(log)}
                          {log.redirected_to && <span className="ra-v2__redirect-to">{log.redirected_to}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={logsPage}
                pageSize={LOGS_PAGE_SIZE}
                total={logsTotal}
                onChange={setLogsPage}
                summary={({ first, last, total }) => t('aiAutomationUi.router.pageSummaryAll', '{{first}}–{{last}} sur {{total}} décisions · toutes périodes', { first, last, total: formatCount(total) })}
                previousLabel={t('common.previousPage', 'Page précédente')}
                nextLabel={t('common.nextPage', 'Page suivante')}
              />
            </>
          )}
        </section>
      </>
    );
  };

  // ---- Dialogs --------------------------------------------------------------

  const renderDrawer = () => {
    const log = selectedLog;
    return (
      <Drawer
        title={t('aiAutomationUi.router.drawer.title', 'Décision du routeur')}
        description={formatStamp(log.created_at)}
        icon={Workflow}
        onClose={() => setSelectedLog(null)}
        closeLabel={t('common.close', 'Fermer')}
        footer={log.ticket_id ? (
          <button type="button" className="u-btn u-btn--primary" onClick={() => navigate(`/tickets/${log.ticket_id}/messages`)}>
            {t('routerAgent.logs.goToTicket', 'Ouvrir le ticket')}
            <ChevronRight aria-hidden="true" />
          </button>
        ) : null}
      >
        <div className="ra-v2__drawer">
          <div className="ra-v2__drawer-decision">
            {decisionBadge(log)}
            {log.topic && <span className="u-tag">{log.topic}</span>}
          </div>
          {log.redirected_to && (
            <Banner tone="info" title={t('aiAutomationUi.router.drawer.redirected', 'Courriel redirigé')}>
              <a className="u-link" href={`mailto:${log.redirected_to}`}>{log.redirected_to}</a>
            </Banner>
          )}
          <DescriptionList
            items={[
              { label: t('routerAgent.logs.source', 'Source'), value: sourceLabel(log.source) },
              { label: t('aiAutomationUi.router.logs.agent', 'Agent IA'), value: log.assistant_name },
              { label: t('routerAgent.logs.brand', 'Marque'), value: log.brand_id ? getBrandName(log.brand_id) : null },
            ]}
          />
          {log.reason && (
            <section className="ra-v2__quote-block">
              <h3 className="u-overline">{t('routerAgent.logs.reason', 'Raison')}</h3>
              <p className="ra-v2__quote">{log.reason}</p>
            </section>
          )}
          {log.customer_message_snippet && (
            <section className="ra-v2__quote-block">
              <h3 className="u-overline">{t('routerAgent.logs.customerMsg', 'Message client')}</h3>
              <p className="ra-v2__quote">{log.customer_message_snippet}</p>
            </section>
          )}
          <details className="u-disclosure ra-v2__ids">
            <summary>
              <ChevronRight aria-hidden="true" />
              {t('routerAgent.logs.technicalIds', 'Identifiants techniques')}
            </summary>
            <DescriptionList
              items={[
                { label: t('routerAgent.logs.ticketId', 'Ticket'), value: <span className="u-code">{log.ticket_id || '—'}</span> },
                { label: t('routerAgent.logs.assistantId', 'Agent'), value: log.assistant_id ? <span className="u-code">{log.assistant_id}</span> : null },
                { label: t('routerAgent.logs.logId', 'Log'), value: <span className="u-code">{log.id}</span> },
              ]}
            />
          </details>
        </div>
      </Drawer>
    );
  };

  const renderTopicModal = () => (
    <Modal
      icon={Tags}
      title={topicModal.editing ? t('routerAgent.editTopic', 'Modifier le sujet') : t('routerAgent.createTopic', 'Créer un sujet')}
      onClose={() => setTopicModal(null)}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={() => setTopicModal(null)} disabled={topicModal.saving}>
            {t('common.cancel', 'Annuler')}
          </button>
          <button type="button" className="u-btn u-btn--primary" onClick={saveTopic} disabled={topicModal.saving}>
            {topicModal.saving ? t('routerAgent.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        {topicModal.error && <Banner tone="danger">{topicModal.error}</Banner>}
        <Field label={t('routerAgent.topicName', 'Nom du sujet')} htmlFor="ra-topic-name">
          <input
            id="ra-topic-name"
            type="text"
            className="u-input"
            value={topicModal.form.name}
            onChange={(e) => setTopicModal((m) => ({ ...m, form: { ...m.form, name: e.target.value }, error: null }))}
            placeholder={t('routerAgent.namePlaceholder', 'Ex. : Avant vente, Après vente, Facturation…')}
          />
        </Field>
        <Field
          label={t('routerAgent.description', 'Description')}
          htmlFor="ra-topic-desc"
          help={t('routerAgent.descriptionHint', 'C’est cette description que le routeur lit pour décider quels tickets classer ici — soyez précis.')}
        >
          <textarea
            id="ra-topic-desc"
            className="u-textarea"
            rows={5}
            maxLength={2000}
            value={topicModal.form.description}
            onChange={(e) => setTopicModal((m) => ({ ...m, form: { ...m.form, description: e.target.value } }))}
            placeholder={t('routerAgent.descriptionPlaceholder', 'Ex. : Questions posées avant l’achat — devis, disponibilité, délais de livraison…')}
          />
        </Field>
      </div>
    </Modal>
  );

  const renderInstrModal = () => {
    const { form, editing, redirectInput, error, saving } = instrModal;
    return (
      <Modal
        wide
        icon={Workflow}
        title={editing ? t('aiAutomationUi.router.instr.editTitle', 'Modifier les instructions') : t('routerAgent.instr.create', 'Créer des instructions')}
        description={t('aiAutomationUi.router.instr.desc', 'Le routeur les lit pour classer chaque nouveau courriel de cette portée.')}
        onClose={() => setInstrModal(null)}
        closeLabel={t('common.close', 'Fermer')}
        footer={(
          <>
            <button type="button" className="u-btn u-btn--secondary" onClick={() => setInstrModal(null)} disabled={saving}>
              {t('common.cancel', 'Annuler')}
            </button>
            <button type="button" className="u-btn u-btn--primary" onClick={saveInstruction} disabled={saving}>
              {saving ? t('routerAgent.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
            </button>
          </>
        )}
      >
        <div className="u-form">
          {error && <Banner tone="danger">{error}</Banner>}
          <Field
            label={t('routerAgent.instr.brand', 'Marque')}
            htmlFor="ra-instr-brand"
            help={editing ? t('aiAutomationUi.router.instr.brandLocked', 'La portée ne change pas après la création.') : undefined}
          >
            {editing ? (
              <input id="ra-instr-brand" className="u-input" value={getBrandName(editing.brand_id)} disabled readOnly />
            ) : (
              <select id="ra-instr-brand" className="u-select" value={form.brand_id} onChange={(e) => setInstrForm({ brand_id: e.target.value })}>
                <option value="">{t('routerAgent.allBrands', 'Toutes les marques')}</option>
                {brands.map((b) => <option key={brandIdOf(b)} value={brandIdOf(b)}>{b.name}</option>)}
              </select>
            )}
          </Field>
          <div className="u-field">
            <span className="u-label">{t('routerAgent.instr.applyToEmails', 'Boîtes mail concernées')}</span>
            {outlookAccounts.length > 0 ? (
              <div className="ra-v2__chips">
                {outlookAccounts.map((acct) => {
                  const email = acct.outlook_email || '';
                  if (!email) return null;
                  const checked = form.apply_to_emails.includes(email);
                  return (
                    <button
                      key={email}
                      type="button"
                      className={`ra-v2__chip${checked ? ' is-on' : ''}`}
                      aria-pressed={checked}
                      onClick={() => setInstrForm({
                        apply_to_emails: checked
                          ? form.apply_to_emails.filter((em) => em !== email)
                          : [...form.apply_to_emails, email],
                      })}
                    >
                      {checked && <Check aria-hidden="true" />}
                      {email}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="ra-v2__muted">{t('routerAgent.instr.noOutlookAccounts', 'Aucune boîte Outlook connectée.')}</p>
            )}
            <p className="u-help">{t('routerAgent.instr.applyToEmailsHelp', 'Restreindre à certaines boîtes Outlook connectées. Vide = tout le courrier entrant de la portée.')}</p>
          </div>
          <Field label={t('routerAgent.instr.sectionBehavior', 'Instructions de routage')} htmlFor="ra-instr-text">
            <textarea
              id="ra-instr-text"
              className="u-textarea"
              rows={8}
              maxLength={100000}
              value={form.instructions}
              onChange={(e) => setInstrForm({ instructions: e.target.value })}
              placeholder={t('routerAgent.instr.instructionsPlaceholder', 'Décrivez comment le routeur doit classer et router les tickets entrants…')}
            />
          </Field>
          <div className="u-field">
            <label className="u-label" htmlFor="ra-instr-redirect">{t('routerAgent.instr.sectionRedirect', 'Redirections')}</label>
            <div className="ra-v2__tags-input">
              {form.redirect_emails.map((email) => (
                <span key={email} className="u-tag ra-v2__removable">
                  {email}
                  <button
                    type="button"
                    onClick={() => setInstrForm({ redirect_emails: form.redirect_emails.filter((e2) => e2 !== email) })}
                    aria-label={t('aiAutomationUi.router.instr.removeRedirect', 'Retirer {{email}}', { email })}
                    title={t('aiAutomationUi.router.instr.removeRedirect', 'Retirer {{email}}', { email })}
                  >
                    <X aria-hidden="true" />
                  </button>
                </span>
              ))}
              <input
                id="ra-instr-redirect"
                type="email"
                value={redirectInput}
                onChange={(e) => setInstrModal((m) => ({ ...m, redirectInput: e.target.value }))}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && redirectInput.trim()) {
                    e.preventDefault();
                    setInstrModal((m) => ({
                      ...m,
                      redirectInput: '',
                      form: { ...m.form, redirect_emails: addRedirect(m.form.redirect_emails, m.redirectInput) },
                    }));
                  }
                }}
                placeholder={t('routerAgent.instr.redirectPlaceholder', 'Saisir une adresse puis Entrée')}
              />
            </div>
            <p className="u-help">{t('routerAgent.instr.redirectEmailsHelp', 'Adresses vers lesquelles le routeur peut transférer un courriel (pas besoin d’être des comptes connectés).')}</p>
          </div>
          <div className="ra-v2__activation">
            <Switch
              id="ra-instr-active"
              checked={form.is_active}
              onChange={(next) => setInstrForm({ is_active: next })}
              label={form.is_active
                ? t('routerAgent.instr.activeLabel', 'Actives — le routage traite cette portée')
                : t('routerAgent.instr.inactiveLabel', 'Désactivées — les tickets de cette portée ne seront pas routés')}
            />
          </div>
        </div>
      </Modal>
    );
  };

  const renderConfirm = () => {
    const { kind, target, busy } = confirm;
    const future = t('aiAutomationUi.router.confirm.future', 'Cela vaut pour les prochains courriels.');
    const scopeAfter = target.brand_id
      ? t('aiAutomationUi.router.confirm.brandAfter', 'Les courriels de {{brand}} suivront les instructions « Toutes les marques » si elles sont actives, sinon ils ne seront plus routés.', { brand: getBrandName(target.brand_id) })
      : t('aiAutomationUi.router.confirm.globalAfter', 'Les courriels des marques sans instructions actives ne seront plus routés.');
    const texts = {
      deactivate: {
        icon: Workflow,
        title: t('aiAutomationUi.router.confirm.deactivateTitle', 'Désactiver les instructions de {{scope}} ?', { scope: getBrandName(target.brand_id) }),
        message: `${scopeAfter} ${future}`,
        label: t('routerAgent.instr.deactivate', 'Désactiver'),
      },
      delete: {
        icon: Trash2,
        title: t('aiAutomationUi.router.confirm.deleteTitle', 'Supprimer les instructions de {{scope}} ?', { scope: getBrandName(target.brand_id) }),
        message: `${scopeAfter} ${t('aiAutomationUi.router.confirm.definitive', 'Les instructions sont effacées.')}`,
        label: t('common.delete', 'Supprimer'),
      },
      deleteTopic: {
        icon: Trash2,
        title: t('aiAutomationUi.router.confirm.deleteTopicTitle', 'Supprimer le sujet « {{name}} » ?', { name: target.name }),
        message: t('aiAutomationUi.router.confirm.deleteTopic', 'Le routeur ne pourra plus l’assigner aux prochains courriels.'),
        label: t('common.delete', 'Supprimer'),
      },
      removeCustom: {
        icon: Trash2,
        title: t('aiAutomationUi.router.confirm.removeCustomTitle', 'Retirer le sujet « {{name}} » ?', { name: target.name }),
        message: t('aiAutomationUi.router.confirm.removeCustom', 'Les nouveaux tickets ne seront plus étiquetés avec ce sujet.'),
        label: t('aiAutomationUi.router.confirm.remove', 'Retirer'),
      },
    }[kind];
    return (
      <ConfirmDialog
        icon={texts.icon}
        title={texts.title}
        message={texts.message}
        confirmLabel={texts.label}
        cancelLabel={t('common.cancel', 'Annuler')}
        onConfirm={confirmChange}
        onCancel={() => setConfirm(null)}
        busy={busy}
      />
    );
  };

  // ══════════ Render ══════════

  const showKpis = stats === undefined || activeTab === 'journal' || (kpis && kpis.total > 0) || (topics && topics.length > 0);

  return (
    <div className="u-page ra-v2">
      <PageHeader title={t('routerAgent.title', 'Agent Routeur')} subtitle={subtitle} />

      <div className="u-well">
        {renderAttention()}
        {showKpis && renderPeriodBar()}
        {showKpis && renderKpis()}
        <Tabs
          label={t('routerAgent.title', 'Agent Routeur')}
          active={activeTab}
          onChange={setActiveTab}
          tabs={[
            { key: 'routing', label: t('routerAgent.routingTab', 'Routage'), icon: Workflow },
            { key: 'classification', label: t('routerAgent.classificationTab', 'Classification'), icon: Shapes },
            { key: 'journal', label: t('routerAgent.journalTab', 'Journal'), icon: ListChecks },
          ]}
        />
        <div role="tabpanel" className="ra-v2__stack">
          {activeTab === 'routing' && renderRouting()}
          {activeTab === 'classification' && renderClassification()}
          {activeTab === 'journal' && renderJournal()}
        </div>
      </div>

      {selectedLog && renderDrawer()}
      {topicModal && renderTopicModal()}
      {instrModal && renderInstrModal()}
      {confirm && renderConfirm()}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default RouterAgent;
